import axios from 'axios';
import i18n from 'i18n';

// Production builds default to the page origin; development to the local backend.
export const BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:8000');

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Header-based JWT, not cookies
});

const authContext = () => {
  // Lazy require breaks circular dependency (authStore imports apiClient).
  const { default: store, selectPrincipal } = require('stores/authStore');
  const state = store.getState();
  return { store, state, principal: selectPrincipal(state) };
};
const changedContext = () => new axios.CanceledError('Authentication context changed.');

// Bind each request before injecting credentials, including refresh retries.
apiClient.interceptors.request.use(
  (config) => {
    const { state, principal } = authContext();
    if (!Object.prototype.hasOwnProperty.call(config, 'originPrincipal')) config.originPrincipal = principal;
    if (config.originPrincipal !== principal) throw changedContext();
    const { accessToken } = state;
    config.headers['Accept-Language'] = i18n.resolvedLanguage === 'ru' ? 'ru' : 'en-us';
    if (accessToken && !config.skipAuth) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else delete config.headers['Authorization'];
    if (config.currentRefresh) {
      const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
      config.data = { ...body, refresh: state.refreshToken };
    }
    return config;
  },
  (error) => { throw error; },
  { synchronous: true }
);

// RESPONSE INTERCEPTOR — handle 401 with token refresh
let refreshSession = null;
let credentialChange = null;

// Pause background 401 handling while an operation replaces the token pair.
// The operation's own expired access token can still take the normal refresh path.
export async function changeCredentials(operation) {
  if (credentialChange) throw new Error('Another security change is in progress.');
  let release;
  const pending = new Promise((resolve) => { release = resolve; });
  credentialChange = pending;
  try {
    return await withRefreshLock(async () => {
      if (typeof navigator !== 'undefined' && navigator.locks?.request) await authContext().store.persist.rehydrate();
      return operation();
    });
  }
  finally {
    if (credentialChange === pending) credentialChange = null;
    release();
  }
}

const withRefreshLock = (operation) => (
  typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request('evolve-auth-refresh', operation)
    : operation()
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry || originalRequest.skipAuth) {
      return Promise.reject(error);
    }
    if (credentialChange && !originalRequest.credentialChange) await credentialChange;
    const { state, principal, store } = authContext();
    if (originalRequest.originPrincipal !== principal) throw changedContext();
    const attempted = originalRequest.headers?.get?.('Authorization') || originalRequest.headers?.Authorization;
    if (state.accessToken && attempted !== `Bearer ${state.accessToken}`) {
      originalRequest._retry = true;
      return apiClient(originalRequest);
    }
    const { refreshToken } = state;
    if (!refreshToken) {
      if (principal !== null) store.getState().logout();
      return Promise.reject(error);
    }
    originalRequest._retry = true;
    if (!refreshSession || refreshSession.principal !== principal || refreshSession.refreshToken !== refreshToken) {
      const session = { principal, refreshToken };
      const stillCurrent = () => {
        const current = authContext();
        return current.principal === principal && current.state.refreshToken === refreshToken;
      };
      const runRefresh = originalRequest.credentialChange ? (operation) => operation() : withRefreshLock;
      session.promise = runRefresh(async () => {
        // A different tab may have rotated while this one waited for the lock.
        if (typeof navigator !== 'undefined' && navigator.locks?.request) await store.persist.rehydrate();
        const latest = authContext();
        if (latest.principal !== principal) throw changedContext();
        if (latest.state.refreshToken !== refreshToken) return latest.state.accessToken;
        const response = await axios.post(`${BASE_URL}/api/v1/auth/token/refresh/`, { refresh: refreshToken });
        if (!stillCurrent()) throw changedContext();
        const { access, refresh } = response.data;
        store.getState().updateTokens({ accessToken: access, refreshToken: refresh });
        return access;
      })
        .catch((refreshError) => {
          if (stillCurrent()) store.getState().logout();
          throw refreshError;
        })
        .finally(() => { if (refreshSession === session) refreshSession = null; });
      refreshSession = session;
    }
    const access = await refreshSession.promise;
    const current = authContext();
    if (current.principal !== originalRequest.originPrincipal || current.state.accessToken !== access) throw changedContext();
    originalRequest.headers['Authorization'] = `Bearer ${access}`;
    return apiClient(originalRequest);
  }
);

export default apiClient;
